import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

interface Props {
  mapUrl: string
  mapFileType: 'pdf' | 'image'
  isOpen: boolean
  onClose: () => void
}

export function GymMapModal({ mapUrl, mapFileType, isOpen, onClose }: Props) {
  const { t } = useTranslation()

  if (!isOpen) return null

  // Portalled to the body: on Home the map button lives in the sticky header,
  // whose blur makes it the frame for anything fixed inside it. The map then
  // opened pinned to the header, with no backdrop behind it.
  return createPortal(
    <div
      className="fixed inset-0 z-[var(--z-overlay)] flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-background p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('gymMap.modalTitle')}</h2>
          <button
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-full text-foreground/50 hover:bg-content2 hover:text-foreground"
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {mapFileType === 'image' ? (
            <img
              src={mapUrl}
              alt={t('gymMap.modalTitle')}
              className="w-full rounded-xl object-contain"
            />
          ) : (
            <iframe
              src={mapUrl}
              title={t('gymMap.modalTitle')}
              className="h-[70vh] w-full rounded-xl border border-divider"
            />
          )}
        </div>

        {/* Some mobile browsers (notably Safari/PWA) can silently fail to
            render a PDF inside an iframe — always offer a working fallback. */}
        <a
          href={mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 text-center text-sm text-primary hover:underline"
        >
          {t('gymMap.openInNewTab')}
        </a>
      </div>
    </div>,
    document.body,
  )
}
